import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Plus, User, Trash2, Edit3, Heart, Calendar, Droplets, AlertCircle } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";
import { useFamily } from "../contexts/FamilyContext";

const RELATIONS = [
    { value: "mother", label: "Mother" },
    { value: "father", label: "Father" },
    { value: "spouse", label: "Spouse" },
    { value: "son", label: "Son" },
    { value: "daughter", label: "Daughter" },
    { value: "brother", label: "Brother" },
    { value: "sister", label: "Sister" },
    { value: "other", label: "Other" },
];
const GENDERS = ["male", "female", "other", "prefer_not_to_say"];

const EMPTY = {
    relation: "mother",
    full_name: "",
    dob: "",
    gender: "",
    blood_group: "",
    emergency_contact_phone: "",
    allergies: "",
    chronic_conditions: "",
};

const FamilyForm = ({ initial, onSubmit, onCancel, submitting }) => {
    const [form, setForm] = useState(() => ({
        ...EMPTY,
        ...initial,
        allergies: Array.isArray(initial?.allergies) ? initial.allergies.join(", ") : initial?.allergies || "",
        chronic_conditions: Array.isArray(initial?.chronic_conditions) ? initial.chronic_conditions.join(", ") : initial?.chronic_conditions || "",
        dob: initial?.dob || "",
    }));

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const submit = (e) => {
        e.preventDefault();
        const payload = {
            relation: form.relation,
            full_name: form.full_name.trim(),
            dob: form.dob || null,
            gender: form.gender || null,
            blood_group: form.blood_group || null,
            emergency_contact_phone: form.emergency_contact_phone || null,
            allergies: form.allergies.split(",").map((s) => s.trim()).filter(Boolean),
            chronic_conditions: form.chronic_conditions.split(",").map((s) => s.trim()).filter(Boolean),
        };
        onSubmit(payload);
    };

    return (
        <form
            onSubmit={submit}
            data-testid="family-form"
            className="space-y-4 rounded-3xl border border-brand-100 bg-brand-50/40 p-6 dark:border-brand-400/20 dark:bg-brand-500/5"
        >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Relation</span>
                    <select name="relation" value={form.relation} onChange={onChange} className="input-clean mt-1" data-testid="fam-relation">
                        {RELATIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Full name</span>
                    <input name="full_name" value={form.full_name} onChange={onChange} required placeholder="e.g. Lakshmi Sharma" className="input-clean mt-1" data-testid="fam-fullname" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Date of birth</span>
                    <input type="date" name="dob" value={form.dob} onChange={onChange} className="input-clean mt-1" data-testid="fam-dob" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Gender</span>
                    <select name="gender" value={form.gender} onChange={onChange} className="input-clean mt-1" data-testid="fam-gender">
                        <option value="">—</option>
                        {GENDERS.map((g) => <option key={g} value={g}>{g.replace("_", " ")}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Blood group</span>
                    <input name="blood_group" value={form.blood_group} onChange={onChange} placeholder="O+" className="input-clean mt-1" data-testid="fam-blood" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Emergency contact</span>
                    <input name="emergency_contact_phone" value={form.emergency_contact_phone} onChange={onChange} placeholder="+91..." className="input-clean mt-1" data-testid="fam-emergency" />
                </label>
                <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Allergies (comma-separated)</span>
                    <input name="allergies" value={form.allergies} onChange={onChange} placeholder="e.g. Penicillin, Sulfa" className="input-clean mt-1" data-testid="fam-allergies" />
                </label>
                <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Chronic conditions (comma-separated)</span>
                    <input name="chronic_conditions" value={form.chronic_conditions} onChange={onChange} placeholder="e.g. Hypertension, Diabetes" className="input-clean mt-1" data-testid="fam-conditions" />
                </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onCancel} className="rounded-full border border-zinc-200 px-4 py-2 text-sm hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5" data-testid="fam-cancel">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-brand !py-2 !px-5 !text-sm" data-testid="fam-save">
                    {submitting ? "Saving..." : "Save member"}
                </button>
            </div>
        </form>
    );
};

const MemberCard = ({ m, onEdit, onDelete, isActive, onSelect }) => (
    <div
        data-testid={`family-card-${m.id}`}
        className={`rounded-2xl border bg-white p-5 transition dark:bg-white/[0.03] ${
            isActive
                ? "border-brand-400 ring-2 ring-brand-200 dark:border-brand-400/60 dark:ring-brand-400/20"
                : "border-zinc-100 hover:border-brand-200 dark:border-white/10 dark:hover:border-brand-400/40"
        }`}
    >
        <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-violet-500 text-white">
                    <User className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
                        {m.is_self ? "Me" : m.full_name}
                    </p>
                    <p className="text-[11px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                        {m.relation}{m.gender ? ` · ${m.gender}` : ""}
                    </p>
                    {m.dob && (
                        <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-zinc-500 dark:text-zinc-400">
                            <Calendar className="h-3 w-3" /> {m.dob}
                        </p>
                    )}
                    {m.blood_group && (
                        <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-zinc-500 dark:text-zinc-400">
                            <Droplets className="h-3 w-3" /> {m.blood_group}
                        </p>
                    )}
                </div>
            </div>
            {!m.is_self && (
                <div className="flex items-center gap-1">
                    <button onClick={() => onEdit(m)} className="rounded-full p-2 text-zinc-500 hover:bg-zinc-50 hover:text-brand-700 dark:hover:bg-white/5" aria-label="Edit" data-testid={`fam-edit-${m.id}`}>
                        <Edit3 className="h-4 w-4" />
                    </button>
                    <button onClick={() => onDelete(m)} className="rounded-full p-2 text-zinc-500 hover:bg-zinc-50 hover:text-rose-500 dark:hover:bg-white/5" aria-label="Delete" data-testid={`fam-delete-${m.id}`}>
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            )}
        </div>
        {(m.allergies?.length || m.chronic_conditions?.length) && (
            <div className="mt-4 space-y-2 border-t border-zinc-100 pt-3 text-xs dark:border-white/10">
                {m.allergies?.length > 0 && (
                    <div className="flex flex-wrap items-start gap-1.5">
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-400">
                            <AlertCircle className="h-3 w-3" /> Allergies
                        </span>
                        {m.allergies.map((a) => (
                            <span key={a} className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">{a}</span>
                        ))}
                    </div>
                )}
                {m.chronic_conditions?.length > 0 && (
                    <div className="flex flex-wrap items-start gap-1.5">
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-brand-700 dark:text-brand-300">
                            <Heart className="h-3 w-3" /> Conditions
                        </span>
                        {m.chronic_conditions.map((c) => (
                            <span key={c} className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">{c}</span>
                        ))}
                    </div>
                )}
            </div>
        )}
        <button
            onClick={() => onSelect(m)}
            data-testid={`fam-set-active-${m.id}`}
            className={`mt-4 w-full rounded-full px-3 py-1.5 text-xs font-medium transition ${
                isActive
                    ? "bg-brand-700 text-white"
                    : "border border-brand-200 text-brand-700 hover:bg-brand-50 dark:border-brand-400/30 dark:text-brand-300 dark:hover:bg-brand-500/10"
            }`}
        >
            {isActive ? "Currently managing" : "Switch to this profile"}
        </button>
    </div>
);

const FamilyContent = () => {
    const { members, activeMember, setActiveId, reload, loading } = useFamily();
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    const onCreate = async (payload) => {
        try {
            setSubmitting(true);
            await api.post("/family/members", payload);
            toast.success("Family member added");
            setAdding(false);
            await reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save");
        } finally {
            setSubmitting(false);
        }
    };

    const onUpdate = async (payload) => {
        try {
            setSubmitting(true);
            await api.patch(`/family/members/${editing.id}`, payload);
            toast.success("Family member updated");
            setEditing(null);
            await reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        } finally {
            setSubmitting(false);
        }
    };

    const onDelete = async (m) => {
        if (!window.confirm(`Remove ${m.full_name} from your family?`)) return;
        try {
            await api.delete(`/family/members/${m.id}`);
            toast.success("Removed");
            await reload();
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <div data-testid="family-page">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="label-uppercase">Family</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">Manage your family profiles</h1>
                    <p className="mt-2 max-w-xl text-sm text-zinc-600 dark:text-zinc-400">
                        Track medications, health metrics, and reminders for everyone in your care. Switch profiles from the top navigation anytime.
                    </p>
                </div>
                {!adding && !editing && (
                    <button onClick={() => setAdding(true)} className="btn-brand !py-2 !px-4 !text-sm" data-testid="family-add-btn">
                        <Plus className="h-4 w-4" /> Add member
                    </button>
                )}
            </div>

            {(adding || editing) && (
                <div className="mb-6">
                    <FamilyForm
                        initial={editing}
                        onSubmit={editing ? onUpdate : onCreate}
                        onCancel={() => { setAdding(false); setEditing(null); }}
                        submitting={submitting}
                    />
                </div>
            )}

            {loading ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3].map((i) => <div key={i} className="h-44 skeleton rounded-2xl" />)}
                </div>
            ) : members.length === 0 ? (
                <EmptyState
                    title="No family members yet"
                    description="Add your spouse, parents, kids — anyone you manage healthcare for."
                    action={<button onClick={() => setAdding(true)} className="btn-brand">Add first member</button>}
                />
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="family-grid">
                    {members.map((m) => (
                        <MemberCard
                            key={m.id}
                            m={m}
                            onEdit={(x) => { setEditing(x); setAdding(false); }}
                            onDelete={onDelete}
                            isActive={activeMember?.id === m.id}
                            onSelect={(x) => { setActiveId(x.id); toast.success(`Now managing ${x.is_self ? "your own profile" : x.full_name}`); }}
                        />
                    ))}
                </div>
            )}

            <div className="mt-10 rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-white/[0.03]">
                <p className="label-uppercase">Quick links</p>
                <div className="mt-3 flex flex-wrap gap-2 text-sm">
                    <button onClick={() => navigate("/caregivers")} className="rounded-full border border-zinc-200 px-4 py-1.5 hover:border-brand-300 hover:text-brand-700 dark:border-white/10 dark:hover:border-brand-400/40" data-testid="family-link-caregivers">Caregivers</button>
                    <button onClick={() => navigate("/chronic-programs")} className="rounded-full border border-zinc-200 px-4 py-1.5 hover:border-brand-300 hover:text-brand-700 dark:border-white/10 dark:hover:border-brand-400/40" data-testid="family-link-chronic">Chronic programs</button>
                    <button onClick={() => navigate("/health")} className="rounded-full border border-zinc-200 px-4 py-1.5 hover:border-brand-300 hover:text-brand-700 dark:border-white/10 dark:hover:border-brand-400/40" data-testid="family-link-health">Health tracker</button>
                </div>
            </div>
        </div>
    );
};

const Family = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <AuthGuard>
                <FamilyContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Family;
